import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setPlan, selectSubscription } from '../Commonlibs/userSlice';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Box, Typography, Container, Paper, Button, Stack, Table, 
  TableBody, TableCell, TableContainer, TableHead, TableRow,
  ThemeProvider, createTheme, useMediaQuery,
  CircularProgress, Snackbar, Alert
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import BuyPlanImage from '../assets/BuyPlan.png';

const Buyplans = () => {
  const [selectedPlan, setSelectedPlan] = useState('professional');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const subscription = useSelector(selectSubscription);
  const user = useSelector(state => state.user.user);
  
  const handlePlanClick = (plan) => {
    setSelectedPlan(plan);
  };
  
  const theme = createTheme({
    typography: {
      fontFamily: 'Outfit, sans-serif',
    },
    components: {
      MuiTypography: {
        styleOverrides: {
          root: {
            fontFamily: 'Outfit, sans-serif',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontFamily: 'Outfit, sans-serif',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            fontFamily: 'Outfit, sans-serif',
          },
        },
      },
    },
  });

  // Plan configurations
  const planConfigs = {
    basic: {
      id: "1",
      name: "Basic",
      price: "₹139",
      features: [
        'Up to 10 property listings',
        'Basic property analytics',
        '30-day listing duration',
        'Chat support',
      ],
      cooling_period: 30
    },
    professional: {
      id: "2",
      name: "Professional",
      price: "₹499",
      features: [
        'Up to 25 property listings',
        'Advanced property analytics',
        '90-day listing duration',
        'Chat support',
        'Priority support',
        'Featured listings'
      ],
      cooling_period: 30
    },
    enterprise: {
      id: "3",
      name: "Enterprise",
      price: "₹999",
      features: [
        'Unlimited property listings',
        'Advanced property analytics',
        '180-day listing duration',
        'Chat support',
        'Priority support',
        'Featured listings',
        'Dedicated account manager',
      ],
      cooling_period: 30
    }
  };

  const handleChoosePlan = async () => {
    if (selectedPlan) {
      try {
        setLoading(true);
        setError(null);
        
        // Get plan ID from the selected plan
        const planId = planConfigs[selectedPlan].id;
        
        // Make API call to create subscription order
        const response = await axios.post('https://propertydealstalk.com/api/seller/createSubscriptionOrder', {
          user_id: user?.id,
          plan_id: planId
        });
        
        // Handle successful response
        if (response.data.statusCode === 200) {
          // Store plan in Redux
          dispatch(setPlan(selectedPlan));
          
          // Redirect to payment page
          window.location.href = response.data.result.redirect_url;
        } else {
          setError('Failed to create subscription order');
        }
      } catch (err) {
        setError('An error occurred while processing your request');
        console.error('Subscription order error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          width: '100%',
          padding: { xs: '30px 0', md: '60px 0' },
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Container 
          maxWidth="lg" 
          sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            textAlign: 'center',
            px: { xs: '12px', sm: '16px' }, 
          }}
        >
          <Paper
            elevation={0} 
            sx={{
              width: '100%', 
              maxWidth: '95%', 
              borderRadius: '16px',
              overflow: 'hidden',
              mt: { xs: 3, md: 5 },
              p: { xs: 2, sm: 3, md: 4 },
              background: 'linear-gradient(0deg, #FFFFFF 86.17%, #F4F2FF 100%)',
            }}
          >
            <Typography
              variant="h4"
              sx={{
                fontFamily: 'Outfit, sans-serif',
                fontWeight: 500,
                fontSize: { xs: '22px', sm: '24px', md: '28px' },
                mb: { xs: 1.5, md: 2 },
                color: '#000000',
              }}
            >
              Upgrade Your Property Listing Experience
            </Typography>
            
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Outfit, sans-serif',
                fontSize: { xs: '13px', sm: '14px', md: '16px' },
                mb: { xs: 2, md: 3 },
                maxWidth: '400px',
                mx: 'auto',
                color: '#626262'  
              }}
            >
              You've used all {subscription.maxProperties} free property listings. Unlock unlimited listings 
              with our premium plans
            </Typography>
            
            <Box
              sx={{
                width: '100%',
                maxWidth: '100%',
                height: 'auto',
                borderRadius: '12px',
                overflow: 'hidden',
                mb: { xs: 3, md: 4 },
                mx: 'auto',
              }}
            >
              <img 
                src={BuyPlanImage} 
                alt="House model with keys" 
                style={{ 
                  width: '100%', 
                  height: 'auto',
                  objectFit: 'cover',
                  display: 'block',
                  borderRadius: '8px',
                }} 
              />
            </Box>
            
            <Box sx={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'center' }}>
              {/* BASIC */}
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '1' },
                  maxWidth: { xs: '100%', md: '28%' },
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  height: { md: '430px' },
                }}
                onClick={() => handlePlanClick('basic')}
              >
                <Box
                  sx={{
                    p: 2,
                    backgroundColor: selectedPlan === 'basic' ? '#2196f3' : 'rgba(244, 242, 255, 1)',
                    color: selectedPlan === 'basic' ? 'white' : 'inherit',
                    pl: 3, // 👈 Adds left padding
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'bold', textAlign: 'left', fontFamily: 'Outfit, sans-serif' }}
                  >
                    Basic
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', mt: 1, textAlign: 'left', fontFamily: 'Outfit, sans-serif' }}
                  >
                    ₹139
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: selectedPlan === 'basic' ? 'rgba(255,255,255,0.8)' : '#626262',
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      /month
                    </Typography>
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {[
                      'Up to 10 property listings',
                      'Basic property analytics',
                      '30-day listing duration',
                      'Chat support',
                    ].map((text, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckIcon sx={{ color: '#2196f3', fontSize: 20, mt: 0.3 }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant={selectedPlan === 'basic' ? 'contained' : 'outlined'}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 1,
                        py: 1,
                        px: 3,
                        minWidth: '150px',
                        backgroundColor: selectedPlan === 'basic' ? 'rgba(250, 204, 21, 1)' : 'transparent',
                        color: selectedPlan === 'basic' ? 'black' : 'primary.main',
                        fontFamily: 'Outfit, sans-serif',
                        '&:hover': {
                          backgroundColor:
                            selectedPlan === 'basic'
                              ? 'rgba(250, 204, 21, 0.9)'
                              : 'rgba(33, 150, 243, 0.1)',
                        },
                      }}
                     onClick={() => handleChoosePlan('1')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'basic' ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Choose Plan'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
              
              {/* PROFESSIONAL */}
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '1' },
                  maxWidth: { xs: '100%', md: '28%' },
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  height: { md: '430px' },
                }}
                onClick={() => handlePlanClick('professional')}
              >
                <Box
                  sx={{
                    p: 2,
                    pl: 3,
                    pr: 3,
                    backgroundColor: selectedPlan === 'professional' ? '#2196f3' : 'rgba(244, 242, 255, 1)',
                    color: selectedPlan === 'professional' ? 'white' : 'inherit',
                  }}
                >
                  {/* Title + Badge inline */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 'bold', textAlign: 'left', fontFamily: 'Outfit, sans-serif' }}
                    >
                      Professional
                    </Typography>
                    <Box
                      sx={{
                        backgroundColor: '#ffc107',
                        color: 'black',
                        px: 1.5,
                        py: 0.5,
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      Most Popular
                    </Box>
                  </Box>
                  {/* Price */}
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', mt: 1, textAlign: 'left', fontFamily: 'Outfit, sans-serif' }}
                  >
                    ₹499
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: selectedPlan === 'professional' ? 'rgba(255,255,255,0.8)' : '#626262',
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      /month
                    </Typography>
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {[
                      'Up to 25 property listings',
                      'Advanced property analytics',
                      '90-day listing duration',
                      'Chat support',
                      'Priority support',
                      'Featured listings',
                    ].map((text, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckIcon sx={{ color: '#2196f3', fontSize: 20, mt: 0.3 }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant={selectedPlan === 'professional' ? 'contained' : 'outlined'}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 1,
                        py: 1,
                        px: 3,
                        minWidth: '150px',
                        backgroundColor:
                          selectedPlan === 'professional' ? 'rgba(250, 204, 21, 1)' : 'transparent',
                        color: selectedPlan === 'professional' ? 'black' : 'primary.main',
                        fontFamily: 'Outfit, sans-serif',
                        '&:hover': {
                          backgroundColor:
                            selectedPlan === 'professional'
                              ? 'rgba(250, 204, 21, 0.9)'
                              : 'rgba(33, 150, 243, 0.1)',
                        },
                      }}
                      onClick={() => handleChoosePlan('2')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'professional' ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Choose Plan'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
              
              {/* ENTERPRISE */}
              <Box
                sx={{
                  flex: { xs: '1 1 100%', md: '1' },
                  maxWidth: { xs: '100%', md: '28%' },
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2,
                  overflow: 'hidden',
                  border: '1px solid #e0e0e0',
                  cursor: 'pointer',
                  backgroundColor: 'white',
                  height: { md: '430px' },
                }}
                onClick={() => handlePlanClick('enterprise')}
              >
                <Box
                  sx={{
                    p: 2,
                    pl: 3, // 👈 Added left padding
                    backgroundColor: selectedPlan === 'enterprise' ? '#2196f3' : 'rgba(244, 242, 255, 1)',
                    color: selectedPlan === 'enterprise' ? 'white' : 'inherit',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 'bold', textAlign: 'left', fontFamily: 'Outfit, sans-serif' }}
                  >
                    Enterprise
                  </Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', mt: 1, textAlign: 'left', fontFamily: 'Outfit, sans-serif' }}
                  >
                    ₹999
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: selectedPlan === 'enterprise' ? 'rgba(255,255,255,0.8)' : '#626262',
                        fontFamily: 'Outfit, sans-serif',
                      }}
                    >
                      /month
                    </Typography>
                  </Typography>
                </Box>
                <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                  <Stack spacing={1.5} sx={{ mb: 3 }}>
                    {[
                      'Unlimited property listings',
                      'Advanced property analytics',
                      '180-day listing duration',
                      'Chat support',
                      'Priority support',
                      'Featured listings',
                      'Dedicated account manager',
                    ].map((text, i) => (
                      <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <CheckIcon sx={{ color: '#2196f3', fontSize: 20, mt: 0.3 }} />
                        <Typography variant="body2">{text}</Typography>
                      </Box>
                    ))}
                  </Stack>
                  <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'center' }}>
                    <Button
                      variant={selectedPlan === 'enterprise' ? 'contained' : 'outlined'}
                      sx={{
                        textTransform: 'none',
                        borderRadius: 1,
                        py: 1,
                        px: 3,
                        minWidth: '150px',
                        backgroundColor:
                          selectedPlan === 'enterprise' ? 'rgba(250, 204, 21, 1)' : 'transparent',
                        color: selectedPlan === 'enterprise' ? 'black' : 'primary.main',
                        fontFamily: 'Outfit, sans-serif',
                        '&:hover': {
                          backgroundColor:
                            selectedPlan === 'enterprise'
                              ? 'rgba(250, 204, 21, 0.9)'
                              : 'rgba(33, 150, 243, 0.1)',
                        },
                      }}
                      onClick={() => handleChoosePlan('3')}
                      disabled={loading}
                    >
                      {loading && selectedPlan === 'enterprise' ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Choose Plan'
                      )}
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box sx={{ mt: 6, mb: 2, width: '100%' }}>
              <Typography 
                variant="h5" 
                sx={{ 
                  fontWeight: 500, 
                  mb: 3, 
                  textAlign: 'center',
                  fontSize: { xs: '20px', sm: '22px', md: '24px' },
                  fontFamily: 'Outfit, sans-serif'
                }}
              >
                Compare Plans
              </Typography>
              
              <Box 
                sx={{ 
                  display: { xs: 'block', sm: 'none' },
                  width: '100%'
                }}
              >
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'Outfit, sans-serif' }}>
                    Property Listings
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Basic:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>10</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Professional:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>25</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Enterprise:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Unlimited</Typography>
                  </Box>
                </Paper>
                
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'Outfit, sans-serif' }}>
                    Listing Duration
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Basic:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>30 days</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Professional:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>90 days</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Enterprise:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>180 days</Typography>
                  </Box>
                </Paper>
                
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'Outfit, sans-serif' }}>
                    Analytics
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Basic:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Basic</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Professional:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Advanced</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Enterprise:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Advanced</Typography>
                  </Box>
                </Paper>
                
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'Outfit, sans-serif' }}>
                    Support
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Basic:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Chat</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Professional:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Chat + Priority</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Enterprise:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Dedicated</Typography>
                  </Box>
                </Paper>
                
                <Paper
                  elevation={1}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 2, fontFamily: 'Outfit, sans-serif' }}>
                    Featured Listings
                  </Typography>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Basic:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>No</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Professional:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Yes - Selective</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif', fontWeight: 500 }}>Enterprise:</Typography>
                    <Typography variant="body2" sx={{ fontFamily: 'Outfit, sans-serif' }}>Yes - All</Typography>
                  </Box>
                </Paper>
              </Box>
              
              <Box sx={{ display: { xs: 'none', sm: 'block' }, width: '100%' }}>
                <TableContainer 
                  component={Paper} 
                  elevation={0}
                  sx={{ 
                    borderRadius: 2,
                    overflow: 'auto',
                    border: 'none',
                    maxWidth: '100%',
                  }}
                >
                  <Table 
                    sx={{ 
                      minWidth: { sm: 650 },
                      tableLayout: { sm: 'fixed', md: 'auto' }
                    }} 
                    aria-label="compare plans table"
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            backgroundColor: '#f8f9fa', 
                            fontWeight: 500,
                            borderBottom: '1px solid #e0e0e0',
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 1, sm: 2 },
                            fontFamily: 'Outfit, sans-serif',
                            width: { sm: '30%', md: '25%' }
                          }}
                        >
                          Features
                        </TableCell>
                        <TableCell 
                          align="center" 
                          sx={{ 
                            backgroundColor: '#f8f9fa', 
                            fontWeight: 500,
                            borderBottom: '1px solid #e0e0e0',
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 1, sm: 2 },
                            fontFamily: 'Outfit, sans-serif',
                            width: { sm: '23.33%', md: '25%' }
                          }}
                        >
                          Basic
                        </TableCell>
                        <TableCell 
                          align="center" 
                          sx={{ 
                            backgroundColor: '#f8f9fa', 
                            fontWeight: 500,
                            borderBottom: '1px solid #e0e0e0',
                            py: { xs: 1.5, sm: 2 },
                            px: { xs: 1, sm: 2 },
                            fontFamily: 'Outfit, sans-serif',
                            width: { sm: '23.33%', md: '25%' }
                          }}
                        >
                          Professional
                        </TableCell>
                                       <TableCell 
                  align="center" 
                  sx={{ 
                    backgroundColor: '#f8f9fa', 
                    fontWeight: 500,
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    width: { sm: '23.33%', md: '25%' }
                  }}
                >
                  Enterprise
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow
                sx={{ '&:nth-of-type(odd)': { backgroundColor: '#ffff'}}}
              >
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ 
                    fontWeight: 400, 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif'
                  }}
                >
                  Property Listings
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  10
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  25
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Unlimited
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ 
                    fontWeight: 400, 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa'
                  }}
                >
                  Listing duration
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  30
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  90
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  180
                </TableCell>
              </TableRow>
              
              <TableRow
                sx={{ '&:nth-of-type(odd)': { backgroundColor: '#ffff'}}}
              >
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ 
                    fontWeight: 400, 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif'
                  }}
                >
                  Analytics
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Basic
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Advanced
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Advanced
                </TableCell>
              </TableRow>
              
              <TableRow>
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ 
                    fontWeight: 400, 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                  }}
                >
                  Support
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Chat
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Chat + Priority
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    backgroundColor: '#f8f9fa',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Dedicated
                </TableCell>
              </TableRow>
              
              <TableRow
                sx={{ '&:nth-of-type(odd)': { backgroundColor: '#ffff'}}}
              >
                <TableCell 
                  component="th" 
                  scope="row"
                  sx={{ 
                    fontWeight: 400, 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif'
                  }}
                >
                  Featured Listings
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  No
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Yes - Selective
                </TableCell>
                <TableCell 
                  align="center"
                  sx={{ 
                    borderBottom: '1px solid #e0e0e0',
                    py: { xs: 1.5, sm: 2 },
                    px: { xs: 1, sm: 2 },
                    fontFamily: 'Outfit, sans-serif',
                    fontSize: { xs: '13px', sm: '14px' }
                  }}
                >
                  Yes - All
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
    <Box sx={{ mt: 6, mb: 2, width: '100%' }}>
      <Typography 
        variant="h5" 
        sx={{ 
          fontWeight: 500, 
          mb: 3, 
          textAlign: 'center',
          fontSize: { xs: '20px', sm: '22px', md: '24px' },
          fontFamily: 'Outfit, sans-serif'
        }}
      >
        Frequently Asked Questions
      </Typography>
      
      <Box sx={{ width: '100%' }}>
        {/* FAQ Item 1 */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            backgroundColor: 'rgba(244, 242, 255, 0.5)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500, 
              mb: 1, 
              fontFamily: 'Outfit, sans-serif',
              fontSize: { xs: '15px', md: '16px' },
              textAlign: 'left'
            }}
          >
            Can I switch plans anytime?
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'Outfit, sans-serif',
              color: '#626262',
              fontSize: { xs: '14px', md: '15px' },
              fontWeight: 400,
              textAlign: 'left'
            }}
          >
            Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.
          </Typography>
        </Paper>
        
        {/* FAQ Item 2 */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            backgroundColor: 'rgba(244, 242, 255, 0.5)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500, 
              mb: 1, 
              fontFamily: 'Outfit, sans-serif',
              fontSize: { xs: '15px', md: '16px' },
              textAlign: 'left'
            }}
          >
            What happens to my current listings?
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'Outfit, sans-serif',
              color: '#626262',
              fontSize: { xs: '14px', md: '15px' },
              fontWeight: 400,
              textAlign: 'left'
            }}
          >
            Your existing listings will remain active until their current expiration date.
          </Typography>
        </Paper>
        
        {/* FAQ Item 3 */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            backgroundColor: 'rgba(244, 242, 255, 0.5)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500, 
              mb: 1, 
              fontFamily: 'Outfit, sans-serif',
              fontSize: { xs: '15px', md: '16px' },
              textAlign: 'left'
            }}
          >
            Do unused listings roll over?
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'Outfit, sans-serif',
              color: '#626262',
              fontSize: { xs: '14px', md: '15px' },
              fontWeight: 400,
              textAlign: 'left'
            }}
          >
            No, listings are based on duration rather than a fixed number. Each listing has its own timeline.
          </Typography>
        </Paper>
        
        {/* FAQ Item 4 */}
        <Paper
          elevation={0}
          sx={{
            mb: 2,
            p: { xs: 2.5, md: 3 },
            borderRadius: 2,
            backgroundColor: 'rgba(244, 242, 255, 0.5)',
            border: '1px solid #e0e0e0',
          }}
        >
          <Typography 
            variant="subtitle1" 
            sx={{ 
              fontWeight: 500, 
              mb: 1, 
              fontFamily: 'Outfit, sans-serif',
              fontSize: { xs: '15px', md: '16px' },
              textAlign: 'left'
            }}
          >
            How do I cancel my subscription?
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'Outfit, sans-serif',
              color: '#626262',
              fontSize: { xs: '14px', md: '15px' },
              fontWeight: 400,
              textAlign: 'left'
            }}
          >
            Please note that once the payment is completed, subscriptions cannot be canceled. Your access will continue until the end of your billing period.
          </Typography>
        </Paper>
      </Box>
    </Box>
  </Paper>
  <Paper
    elevation={0}
    sx={{
      mt: 6, 
      mb: 4, 
      width: '90%',
      backgroundColor: 'rgba(249, 250, 251, 1)',
      borderRadius: '16px',
      overflow: 'hidden',
      p: { xs: 3, md: 4 }
    }}
  >
    <Typography 
      variant="h5" 
      sx={{ 
        fontWeight: 500, 
        mb: 5, 
        textAlign: 'center',
        fontSize: { xs: '20px', sm: '22px', md: '24px' },
        fontFamily: 'Outfit, sans-serif',
      }}
    >
      Why Choose Our Premium Plans?
    </Typography>
    
    <Box 
      sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' },
        justifyContent: 'space-around',
        alignItems: 'center',
        px: { xs: 1, sm: 3, md: 4 },
        gap: { xs: 4, md: 2 },
        mb: 5
      }}
    >
      {/* Advanced Analytics */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          maxWidth: '250px',
          textAlign: 'center'
        }}
      >
        <Box 
          sx={{ 
            color: '#4285F4', 
            fontSize: '36px', 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Analytics Icon - can use Material UI icon or svg */}
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M28 28H4V4H6V24H28V28Z" fill="#4285F4"/>
            <path d="M10 18H8V22H10V18Z" fill="#4285F4"/>
            <path d="M15 14H13V22H15V14Z" fill="#4285F4"/>
            <path d="M20 10H18V22H20V10Z" fill="#4285F4"/>
            <path d="M25 6H23V22H25V6Z" fill="#4285F4"/>
          </svg>
        </Box>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 500, 
            mb: 1,
            fontFamily: 'Outfit, sans-serif'
          }}
        >
          Advanced Analytics
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#626262',
            fontFamily: 'Outfit, sans-serif',
            fontSize: '14px'
          }}
        >
          Track property views, inquiries, and engagement metrics
        </Typography>
      </Box>
      
      {/* Premium Visibility */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          maxWidth: '250px',
          textAlign: 'center'
        }}
      >
        <Box 
          sx={{ 
            color: '#4285F4', 
            fontSize: '36px', 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Star Icon */}
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2L20.5 11.2L30.5 12.5L23 19.8L25 30L16 25L7 30L9 19.8L1.5 12.5L11.5 11.2L16 2Z" fill="#4285F4"/>
          </svg>
        </Box>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 500, 
            mb: 1,
            fontFamily: 'Outfit, sans-serif'
          }}
        >
          Premium Visibility
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#626262',
            fontFamily: 'Outfit, sans-serif',
            fontSize: '14px'
          }}
        >
          Get featured placement in search results
        </Typography>
      </Box>
      
      {/* Dedicated Support */}
      <Box 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          maxWidth: '250px',
          textAlign: 'center'
        }}
      >
        <Box 
          sx={{ 
            color: '#4285F4', 
            fontSize: '36px', 
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {/* Shield Icon */}
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2L4 7V15C4 22.1797 9.12031 28.8094 16 30C22.8797 28.8094 28 22.1797 28 15V7L16 2Z" fill="#4285F4"/>
          </svg>
        </Box>
        <Typography 
          variant="subtitle1" 
          sx={{ 
            fontWeight: 500, 
            mb: 1,
            fontFamily: 'Outfit, sans-serif'
          }}
        >
          Dedicated Support
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            color: '#626262',
            fontFamily: 'Outfit, sans-serif',
            fontSize: '14px'
          }}
        >
          Priority assistance from our real estate experts
        </Typography>
      </Box>
    </Box>
  </Paper>
</Container>
{/* Testimonial */}
<Box 
  sx={{ 
      width:'83%',
    backgroundColor: 'rgba(247, 245, 255, 1)',
    py: 3,
    px: { xs: 2, sm: 4 },
    mx: { xs: 2, sm: 4, md: 6 },
    mb: 2,
    borderRadius: 2,
    textAlign: 'center'
  }}
>
  <Typography 
    variant="body1" 
    sx={{ 
      fontStyle: 'italic',
      color: '#626262',
      mb: 1,
      fontFamily: 'Outfit, sans-serif',
      fontSize: '15px'
    }}
  >
    "PropertyDeals's premium features helped me close deals 50% faster"
  </Typography>
  <Typography 
    variant="body2" 
    sx={{ 
      fontWeight: 500,
      fontFamily: 'Outfit, sans-serif'
    }}
  >
    John Smith, Real Estate Agent
  </Typography>
</Box>

{/* Error/Success notifications */}
<Snackbar 
  open={!!error} 
  autoHideDuration={6000} 
  onClose={() => setError(null)}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
  <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
    {error}
  </Alert>
</Snackbar>

<Snackbar 
  open={success} 
  autoHideDuration={6000} 
  onClose={() => setSuccess(false)}
  anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
>
  <Alert onClose={() => setSuccess(false)} severity="success" sx={{ width: '100%' }}>
    Subscription successful! Redirecting to add property page...
  </Alert>
</Snackbar>
</Box>
</ThemeProvider>
);
};

export default Buyplans;